import { mergeBufferGeometries } from 'three-stdlib'
import {
  Object3D,
  BufferGeometry,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  EdgesGeometry,
} from 'three'

import { frontMaterialProperties, backMaterialProperties } from './materials'
import {
  BACK_RENDER_ORDER,
  FRONT_RENDER_ORDER,
  WIRE_RENDER_ORDER,
} from '@/pages/CADPage/constants'
import { ASMTreeNode } from '@/state'
import { GLTFObject } from './GLTFObject'

/**
 * Merges all part meshes of the given Object3D that share the same material into a single mesh.
 * This function traverses the object hierarchy, collects geometries by their materials,
 * and attempts to merge them. If successful, it replaces the original meshes with a new
 * merged mesh and disposes of the old geometries and materials.
 *
 * @param obj - The Object3D instance whose part meshes are to be merged.
 * @returns return number of meshes merged
 */
export function mergePartMeshes(this: GLTFObject, obj: Object3D): number {
  let nMeshes = 0
  if (obj.userData?.isMerged) return nMeshes

  const materialGeometryMap = new Map<
    MeshStandardMaterial,
    { geometries: BufferGeometry[]; meshes: Mesh[] }
  >()

  obj.traverse((child) => {
    if (child instanceof Mesh) {
      nMeshes++
      const material = child.material as MeshStandardMaterial
      if (materialGeometryMap.has(material)) {
        materialGeometryMap.get(material)?.geometries.push(child.geometry)
        materialGeometryMap.get(material)?.meshes.push(child)
      } else {
        materialGeometryMap.set(child.material, {
          geometries: [child.geometry],
          meshes: [child],
        })
      }
    }
  })

  // Get the base name of the object
  const partBaseName = obj?.name || obj?.uuid
  if (!obj.name) console.warn('Object has no name, using UUID: ', obj.toJSON())

  // Attempt to merge geometries of the same material
  let nMeshesMerged = 0
  materialGeometryMap.forEach(({ geometries, meshes }, material) => {
    const mergedGeometry = mergeBufferGeometries(geometries)
    if (!mergedGeometry) {
      console.error('Failed to merge geometries: ', obj.name)
      return 0
    }

    // Create front side mesh with merged geometry
    material.setValues(frontMaterialProperties)
    material.needsUpdate = true
    const mergedMesh = new Mesh(mergedGeometry, material)
    const suffix = nMeshesMerged > 0 ? `_${nMeshesMerged}` : ''

    mergedMesh.name = `FrontSide:${partBaseName}${suffix}`
    mergedMesh.renderOrder = FRONT_RENDER_ORDER
    mergedMesh.userData.isFrontSide = true
    nMeshesMerged++
    obj.add(mergedMesh)
    obj.userData.isMerged = true

    // Clean up old meshes
    meshes.forEach((mesh) => {
      obj.remove(mesh)
      mesh.geometry.dispose()
    })
  })

  // Merge back side geometries
  let color: number | null = null
  let materialKey = ''
  const backGeometries: BufferGeometry[] = []
  obj.traverse((mesh) => {
    if (mesh instanceof Mesh) {
      if (!materialKey) materialKey = `${mesh.material.name}_back`
      if (color === null) color = mesh.material.color.getHex()
      const backGeometry = mesh.geometry.clone()
      backGeometries.push(backGeometry)
    }
  })

  const backGeometry =
    backGeometries.length > 1
      ? mergeBufferGeometries(backGeometries)
      : backGeometries[0]
  if (!backGeometry) {
    console.warn('Failed to merge back geometries: ', obj.name)
    return 0
  }

  const colorKey: number = color !== null ? color : 0x000000
  const backSideMaterial =
    this.materialMap.get(materialKey) ??
    this.materialMap
      .set(
        materialKey,
        new MeshBasicMaterial({
          name: materialKey,
          color: colorKey,
          ...backMaterialProperties,
        }),
      )
      .get(materialKey)

  const backMesh = new Mesh(backGeometry, backSideMaterial)
  backMesh.name = `BackSide:${partBaseName}`
  backMesh.userData.isBackSide = true
  backMesh.renderOrder = BACK_RENDER_ORDER
  obj.add(backMesh)

  return nMeshes
}

const edgesCache = new WeakMap<BufferGeometry, EdgesGeometry>()

export function buildPartWireframe(this: GLTFObject, obj: Object3D) {
  const lineGeometries: BufferGeometry[] = []

  obj.traverse((child) => {
    if (child.type === 'Mesh') {
      const mesh = child as Mesh

      // get or build cached EdgesGeometry for this source geometry
      let cached = edgesCache.get(mesh.geometry)
      if (!cached) {
        cached = new EdgesGeometry(mesh.geometry, 180)
        edgesCache.set(mesh.geometry, cached)
      }

      // use a clone so we can dispose after merging without touching the cache
      lineGeometries.push(cached.clone())
    }
  })

  // Merge all line geometries
  if (lineGeometries.length > 0) {
    const mergedGeometry = mergeBufferGeometries(lineGeometries)
    if (!mergedGeometry) {
      console.error('Failed to merge wireframe geometries: ', obj.name)
      return
    }

    const mergedLine = new LineSegments(mergedGeometry, this.lineMaterial)
    mergedLine.name = `WireFrame:${obj.name}`
    mergedLine.frustumCulled = true
    mergedLine.renderOrder = WIRE_RENDER_ORDER

    // Dispose of the old geometries
    lineGeometries.forEach((geo) => geo.dispose())
    return mergedLine
  }
}

export function isLeafNode(node: ASMTreeNode): boolean {
  return node.children.length === 0
}
